import { Button } from "@sellar-io/components";
import { PageContainer } from "../../components/pageContainer/PageContainer";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { ReactElement, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Icon, SpinnerLegacy } from "../../components";
import { Alert } from "../../components/alert/Alert";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import {
  DELIVERY_METHOD_CHARGES,
  DELIVERY_SCHEDULES,
  NEW_DELIVERY_AREA,
  CUSTOMER_ZONES,
} from "../../constants/routes";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { useDeliveryAreaCreate } from "../../hooks/api/v2/deliveryArea/useDeliveryAreaCreate";
import { useDeliveryAreaCreateContext } from "../../hooks/useDeliveryAreaCreateContext.shared";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { DeliveryAreaForm } from "./DeliveryAreaForm";
import { DeliveryAreaFormRef } from "./DeliveryAreaForm.shared";
import { useFocusedNavigation } from "../../hooks/useFocusedNavigation";

dayjs.extend(utc);
export function DeliveryAreaCreate(): ReactElement {
  const navigate = useNavigate();
  const { postcodes, selectedDays } = useDeliveryAreaCreateContext();
  const [postcodeError, setPostcodeError] = useState<boolean>(false);

  const remoteFeatureFlags = useRemoteFeatureFlags();
  const unbundlingEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );

  useFocusedNavigation(!unbundlingEnabled);

  const formRef = useRef<DeliveryAreaFormRef>(null);

  const { state } = useLocation();
  const redirectToNewDeliverySchedule =
    state?.redirectToNewDeliverySchedule ?? null;

  const createDeliveryAreaMutation = useDeliveryAreaCreate();

  return (
    <>
      <NavigationBar
        title={
          unbundlingEnabled ? "New delivery schedule" : "New delivery area"
        }
        leftIcon={"CLOSE"}
        onClickLeftIcon={() => {
          if (unbundlingEnabled) {
            if (formRef.current?.isDirty()) {
              formRef.current?.submitForm();
            } else {
              navigate(DELIVERY_SCHEDULES);
            }
            return;
          }
          if (redirectToNewDeliverySchedule) {
            navigate(DELIVERY_SCHEDULES);
          } else {
            navigate(DELIVERY_METHOD_CHARGES.href({ method: "direct" }));
          }
        }}
      />
      <PageContainer className={"flex flex-col gap-4"}>
        {createDeliveryAreaMutation.isPending ? (
          <SpinnerLegacy />
        ) : (
          <>
            {createDeliveryAreaMutation.isError && (
              <Alert
                type={"error"}
                message={
                  apiV2AssertError(createDeliveryAreaMutation.error)?.response
                    ?.data.message ?? "An unknown error occurred"
                }
              />
            )}
            {!createDeliveryAreaMutation.isError && (
              <Alert
                className={"w-full"}
                type={"information"}
                title={"Need help?"}
                message={"Read our setup guide including walkthrough video."}
              >
                <Button
                  color={"tertiary"}
                  className={"border-blue-300 bg-blue-100 text-blue-800"}
                  onClick={() =>
                    window.open(
                      "https://www.loom.com/share/5d15d46d9fb04ccb95206c3d75fe8b46",
                      "_blank",
                    )
                  }
                >
                  <Icon icon={"OPEN_IN_NEW"} />
                  View guide
                </Button>
              </Alert>
            )}
            <DeliveryAreaForm
              ref={formRef}
              hideActions={unbundlingEnabled}
              onSubmit={(data, options) => {
                if (!postcodes.length) return setPostcodeError(true);
                setPostcodeError(false);
                createDeliveryAreaMutation.mutate(
                  {
                    startAt: dayjs(data.startAt).toDate(),
                    name: data.name ?? "",
                    postcodes,
                    weeklyCadence: parseInt(
                      data.weeklyCadence?.toString() ?? "",
                    ),
                    days: selectedDays,
                    orderCutOffTime: data.orderCutOffTime
                      ? dayjs
                          .utc(data.orderCutOffTime)
                          .set("hour", dayjs(data.orderCutOffTime).hour())
                          .toDate()
                      : null,
                    orderCutOffDaysBefore: data.orderCutOffDaysBefore,
                    includeWeekend: data.includeWeekend,
                  },
                  {
                    onSuccess: () => {
                      if (options?.createAnother) {
                        navigate(NEW_DELIVERY_AREA, {
                          state: { redirectToCustomerZones: true },
                        });
                      } else if (options?.redirectToCustomerZones) {
                        navigate(CUSTOMER_ZONES);
                      } else if (
                        redirectToNewDeliverySchedule ||
                        unbundlingEnabled
                      ) {
                        navigate(DELIVERY_SCHEDULES);
                      } else {
                        navigate(
                          DELIVERY_METHOD_CHARGES.href({ method: "direct" }),
                        );
                      }
                    },
                  },
                );
              }}
              postcodeError={postcodeError}
            />
          </>
        )}
      </PageContainer>
    </>
  );
}
